import { OPS_AUTH, USE_MOCK_AUTH } from "../config/api";
import { apiRequest, unwrapApiEnvelope } from "./apiClient";

/** Provider portal roles that appear in shared all_roles. */
export const PROVIDER_ROLE_NAMES = new Set(["owner", "manager", "analyst", "viewer"]);

/**
 * Flatten permissions from API role payload into permission name keys.
 * Supports array of objects OR module→string[] map.
 */
export function flattenRolePermissionNames(permissions) {
  if (!permissions) return [];
  if (Array.isArray(permissions)) {
    return permissions
      .map((item) => (typeof item === "string" ? item : item?.name))
      .filter(Boolean);
  }
  if (typeof permissions === "object") {
    return Object.values(permissions)
      .flat()
      .map((item) => (typeof item === "string" ? item : item?.name))
      .filter(Boolean);
  }
  return [];
}

export function normalizeOpsRole(raw) {
  if (!raw) return null;
  const name = raw.name ?? "";
  const roleSlug = raw.role_slug ?? null;
  const permissionNames = flattenRolePermissionNames(raw.permissions);
  const isProvider = PROVIDER_ROLE_NAMES.has(name);
  const isSuperAdmin = name === "super_admin";

  return {
    id: roleSlug || name,
    name,
    role_slug: roleSlug,
    label: formatRoleLabel(name),
    permissions: permissionNames,
    permission_objects: Array.isArray(raw.permissions) ? raw.permissions : [],
    created_at: raw.created_at ?? null,
    system: isKnownSystemRole(name),
    locked: isSuperAdmin,
    provider: isProvider,
  };
}

function formatRoleLabel(name) {
  if (!name) return "Role";
  if (name.includes(" ")) return name;
  return String(name)
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function isKnownSystemRole(name) {
  return (
    name === "super_admin" ||
    name === "commercial_executive" ||
    name === "executive_manager" ||
    name === "call_center_manager" ||
    name === "call_center_agent" ||
    name === "legal_officer" ||
    PROVIDER_ROLE_NAMES.has(name)
  );
}

let rolesCache = { token: null, promise: null, result: null };

/**
 * GET /api/ops/all_roles — returns every role with its permissions.
 */
export async function fetchOpsRoles(token) {
  if (USE_MOCK_AUTH || !token) {
    return [];
  }

  if (rolesCache.token === token && rolesCache.result) {
    return rolesCache.result;
  }

  if (rolesCache.token === token && rolesCache.promise) {
    return rolesCache.promise;
  }

  rolesCache = {
    token,
    result: null,
    promise: (async () => {
      const json = await apiRequest(OPS_AUTH.allRoles, {
        method: "GET",
        token,
      });
      const data = unwrapApiEnvelope(json);
      const rows = Array.isArray(data)
        ? data
        : (data?.roles ?? data?.data ?? []);
      const list = (Array.isArray(rows) ? rows : [])
        .map(normalizeOpsRole)
        .filter(Boolean);
      rolesCache = { token, promise: null, result: list };
      return list;
    })().catch((err) => {
      rolesCache = { token: null, promise: null, result: null };
      throw err;
    }),
  };

  return rolesCache.promise;
}

export function clearOpsRolesCache() {
  rolesCache = { token: null, promise: null, result: null };
}

/**
 * POST /api/ops/create_roles
 * Body: { name, permissions: permission_slug[] }
 */
export async function createOpsRole(token, { name, permissions }) {
  if (!name?.trim()) {
    throw new Error("Role name is required.");
  }
  if (!permissions?.length) {
    throw new Error("Select at least one permission to create a role.");
  }

  if (USE_MOCK_AUTH || !token) {
    return normalizeOpsRole({
      name: name.trim(),
      role_slug: `local_${Date.now()}`,
      permissions: permissions.map((slug) => ({
        name: slug,
        permission_slug: slug,
      })),
      created_at: new Date().toISOString(),
    });
  }

  const json = await apiRequest(OPS_AUTH.createRole, {
    method: "POST",
    token,
    body: {
      name: name.trim(),
      permissions: [...permissions],
    },
  });

  clearOpsRolesCache();
  return normalizeOpsRole(unwrapApiEnvelope(json));
}

/**
 * PUT /api/ops/update_roles/{role_slug}
 * Body: { name, permissions: permission_slug[] }
 */
export async function updateOpsRole(token, roleSlug, { name, permissions }) {
  if (!roleSlug) {
    throw new Error("Role slug is required.");
  }
  if (!name?.trim()) {
    throw new Error("Role name is required.");
  }
  if (!permissions?.length) {
    throw new Error("A role must keep at least one permission.");
  }

  if (USE_MOCK_AUTH || !token) {
    return normalizeOpsRole({
      name: name.trim(),
      role_slug: roleSlug,
      permissions: permissions.map((slug) => ({
        name: slug,
        permission_slug: slug,
      })),
    });
  }

  const json = await apiRequest(`${OPS_AUTH.updateRole}/${roleSlug}`, {
    method: "PUT",
    token,
    body: {
      name: name.trim(),
      label: name.trim(),
      permissions: [...permissions],
    },
  });

  clearOpsRolesCache();
  return normalizeOpsRole(unwrapApiEnvelope(json));
}

/**
 * DELETE /api/ops/delete_roles/{role_slug}
 */
export async function deleteOpsRole(token, roleSlug) {
  if (!roleSlug) {
    throw new Error("Role slug is required.");
  }

  if (USE_MOCK_AUTH || !token) {
    return true;
  }

  await apiRequest(`${OPS_AUTH.deleteRole}/${roleSlug}`, {
    method: "DELETE",
    token,
  });

  clearOpsRolesCache();
  return true;
}
